import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Progress } from "@shared/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";

import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useAuditUsers,
  useValidationBreakList,
  usePostMeterCycleAuditUserMapping,
  usePutMeterCycleAuditUserMapping,
} from "@features/mx/validations/hooks";
import { useUserUtilityServices } from "@features/mx/settings/hooks";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { FormField } from "@shared/services/FormServices";
import { logEvent } from "@shared/analytics/analytics";

import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useDebounce } from "@shared/hooks/useDebounce";

import CycleFilters from "../ActiveCycles/CycleFilters";
import ManageAssignmentDialog from "../ManageAssignmentDialog";

function AssignmentCell({
  type,
  cycle,
  refetchCycle,
}: {
  type: "validator" | "supervisor";
  cycle: any;
  refetchCycle: () => void;
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [auditUserSearch, setAuditUserSearch] = useState("");
  const remoteUtilityId = useRemoteUtilityId();
  const userRoleType = type === "validator" ? 0 : 2;

  const { data: usersList, refetch } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog,
    cycle.cycleId,
    auditUserSearch
  );

  useEffect(() => {
    if (openDialog) refetch();
  }, [openDialog]);

  const postMutation = usePostMeterCycleAuditUserMapping(userRoleType, cycle?.cycleId);
  const putMutation = usePutMeterCycleAuditUserMapping(userRoleType, cycle?.cycleId);

  const handleAssign = (row: any) => {
    const payload = {
      meterCycle: cycle?.cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    postMutation.mutate(payload, {
      onSuccess: () => {
        logEvent(`MX ${type === "validator" ? "Validator" : "Supervisor"} Added`, payload);
        refetchCycle?.();
      },
    });
  };

  const handleUnassign = (row: any) => {
    const payload = {
      meter_cycle: cycle?.cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    putMutation.mutate(payload, {
      onSuccess: () => {
        logEvent(`MX ${type === "validator" ? "Validator" : "Supervisor"} Removed`, payload);
        refetchCycle?.();
      },
    });
  };

  const infoList = type === "validator" ? cycle.validatorInfo : cycle.supervisorInfo;
  const nameKey = type === "validator" ? "validatorName" : "supervisorName";

  return (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-pointer font-semibold w-6 text-center text-sm">
            {Array.isArray(infoList) ? infoList.length : 0}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {Array.isArray(infoList) && infoList.length > 0
            ? infoList.map((v: any) => v[nameKey]).join(", ")
            : `No ${type}s`}
        </TooltipContent>
      </Tooltip>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-gray-500 hover:text-gray-900"
        onClick={(e) => {
          e.preventDefault();
          setOpenDialog(true);
        }}
      >
        <Settings size={16} />
      </Button>
      {openDialog && (
        <ManageAssignmentDialog
          open={openDialog}
          onOpenChange={(open) => !open && setOpenDialog(false)}
          type={type}
          data={usersList}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
          onSearch={(search) => setAuditUserSearch(search)}
        />
      )}
    </div>
  );
}

function ActiveCycles({
  onCycleCountChange,
}: {
  onCycleCountChange?: (data: { total: number; shown: number }) => void;
}) {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const rowKeyMap = useMemo(() => new WeakMap(), []);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState("");

  const search = useDebounce(searchTerm, 300);

  const { data: activeCycles, isFetching: isLoading, refetch: refetchCycles } = useValidationBreakList({
    remoteUtilityId,
    page: currentPage,
    limit: PAGE_SIZE,
    filters,
    search,
    isCompleted: false,
  });

  useEffect(() => {
    if (activeCycles) {
      onCycleCountChange?.({
        total: activeCycles?.count || 0,
        shown: activeCycles?.results?.length || 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCycles?.count, activeCycles?.results?.length]);

  const { data: utilityServices } = useUserUtilityServices(remoteUtilityId);
  const { data: readTypes } = useChoices({ choice: "meter_type" });
  const { data: validatorsList } = useAuditUsers(remoteUtilityId, 0, showFilters);
  const { data: supervisorList } = useAuditUsers(remoteUtilityId, 2, showFilters);

  const validatorDropdownOptions = mapToLabelValue(validatorsList, "name", "id");
  const supervisorDropdownOptions = mapToLabelValue(supervisorList, "name", "id");

  const activeUtilityServices = utilityServices?.filter((service) => service.isActive);
  const utilityServicesList = mapToLabelValue(activeUtilityServices, "name", "name");
  const readTypeList = mapToLabelValue(readTypes, "value", "key");

  const filterFields: FormField[] = [
    { label: "Start Date", name: "start_date", type: "datepicker" },
    { label: "End Date", name: "end_date", type: "datepicker" },
    {
      label: "Read Type",
      name: "meter_type",
      type: "select",
      options: readTypeList,
    },
    {
      label: "Utility",
      name: "utility_service",
      type: "select",
      options: utilityServicesList,
    },
    {
      label: "Validator",
      name: "validator_id",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      options: validatorDropdownOptions,
    },
    {
      label: "Supervisor",
      name: "supervisor_id",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      options: supervisorDropdownOptions,
    },
  ];

  const handleSubmit = (values: any) => {
    setFilters(values);
    setCurrentPage(1);
  };

  const handleNavigation = (row: any) => {
    switch (row.dataCollectionMethod?.toLowerCase()) {
      case "mobile":
        navigate(`/mx/meter-validation/photometer-validation/view/${row.runId}`);
        break;
      case "manual":
        navigate(`/mx/meter-validation/manual-meter-validation/view/${row.runId}`);
        break;
      case "automated":
        toast({ title: "Info", description: "Automated reading is getting ready" });
        break;
      default:
        toast({ title: "Unsupported Type", description: "This reading method is not recognized." });
        break;
    }
  };

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "meterCycleName",
        header: "Cycle Details",
      },
      {
        key: "dateRange",
        header: "Date Range",
        type: "custom",
        render: (_, row) => (
          <div className="text-xs text-gray-600 whitespace-nowrap">
            {row.createdDate || row.startDate} - {row.validationEnd || row.endDate}
          </div>
        ),
      },
      {
        key: "uniqueMeterCount",
        header: "Total",
        type: "text",
      },
      {
        key: "collectedCount",
        header: "Collected",
        type: "text",
      },
      {
        key: "pendingCount",
        header: "Missing",
        type: "text",
      },
      {
        key: "validatedCount",
        header: "Validated",
        type: "text",
      },
      {
        key: "exemptedCount",
        header: "Exempted",
        type: "text",
      },
      {
        key: "dataCollectionMethod",
        header: "Method",
        type:'badge'
      },
      {
        key: "status",
        header: "Status",
        type:'badge'
      },
      {
        key: "validators",
        header: "Validators",
        type: "custom",
        render: (_, row) => (
          <AssignmentCell type="validator" cycle={row} refetchCycle={refetchCycles} />
        ),
      },
      {
        key: "supervisors",
        header: "Supervisors",
        type: "custom",
        render: (_, row) => (
          <AssignmentCell type="supervisor" cycle={row} refetchCycle={refetchCycles} />
        ),
      },
    ],
    [refetchCycles]
  );

  const actions: TableAction[] = [
    {
      label: "View Cycle",
      onClick: handleNavigation,
      icon: "Eye",
      variant: "outline",
    },
  ];

  const totalItems = activeCycles?.count || 0;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems: totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalItems]
  );

  return (
    <div className="relative">
      <GenericTable
        data={activeCycles?.results ?? []}
        loading={isLoading}
        columns={columns}
        rowKey={(row) => {
          const baseKey = `${row.scheduleId}-${row.cycleId}`;
          if (!rowKeyMap.has(row)) {
            rowKeyMap.set(row, `${baseKey}-${Math.random().toString(36).substr(2, 9)}`);
          }
          return rowKeyMap.get(row);
        }}
        actions={actions}
        actionsType="icons"
        pagination={pagination}
        onPageChange={(page) => setCurrentPage(page)}
        emptyMessage="No Active Cycles"
        onSearch={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search cycles..."
        showFilters={showFilters}
        onShowFiltersChange={setShowFilters}
        filterPanel={
          <CycleFilters filterFields={filterFields} onSubmit={handleSubmit} />
        }
      />
    </div>
  );
}

export default ActiveCycles;
